import { Trans, useLingui } from '@lingui/react/macro'
import { useRouter } from '@tanstack/react-router'
import { LucideIcon } from 'components/LucideIcon'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'designSystem/components/DropdownMenu'
import {
  SidebarMenuButton,
  SidebarMenuItem,
} from 'designSystem/components/Sidebar'
import { useIsMobile } from 'designSystem/hooks/useMobile'
import { useSheetPortalHost } from 'designSystem/hooks/useSheetPortalHost'
import type { NavItemConfig } from 'features/navbar/navConfig'
import type { SelectedTab } from 'features/navbar/types'

interface EmbeddedSidebarMoreFeaturesMenuProps {
  items: Array<[SelectedTab, NavItemConfig]>
  onNavigate: (destinationKey: string, label: string, href: string) => void
  orgID: string | undefined
  siteID: string | undefined
}

export function EmbeddedSidebarMoreFeaturesMenu({
  items,
  onNavigate,
  orgID,
  siteID,
}: EmbeddedSidebarMoreFeaturesMenuProps) {
  const { i18n, t } = useLingui()
  const router = useRouter()
  const isMobile = useIsMobile()
  const portalHost = useSheetPortalHost(isMobile)

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <SidebarMenuButton asChild size='sm' variant='embedded'>
          <DropdownMenuTrigger aria-label={t`More features`}>
            <LucideIcon aria-hidden='true' name='Ellipsis' />
            <span>
              <Trans>More</Trans>
            </span>
          </DropdownMenuTrigger>
        </SidebarMenuButton>
        {portalHost.hostNode}
        <DropdownMenuContent
          align='start'
          side='bottom'
          layout='padded'
          width='lg'
          container={portalHost.container}>
          {items.map(([tabKey, config]) => {
            const label = i18n._(config.label)
            const Icon = config.icon
            const handleSelect = () => {
              const { href } = router.buildLocation({
                to: config.to,
                params: config.params?.(siteID ?? '', orgID),
                search: config.search?.(siteID ?? '', orgID),
              })
              onNavigate(tabKey, label, href)
            }
            return (
              <DropdownMenuItem key={tabKey} size='xs' onSelect={handleSelect}>
                <Icon />
                {label}
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  )
}
